<script setup lang="ts">
import { track } from '@/lib/usageTracker'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { type CustomerTaskDetailDTO, type TaskDetail, type TaskSummaryDto, type CustomerAssociationRow } from '@/api/lytAccountingClient'
import { postCustomerAsync, postCustomersUnassociated, type QboCustomerSearchResult, searchQboCustomersPaged, getCustomerLink, postCustomerLink } from '@/api/lytAccountingClient'
import { Alert, Badge, Button, Pagination, TabNav, SearchInput } from '@/components/ui'
import TaskDetailDrawer from '@/components/task/TaskDetailDrawer.vue'
import EntityAssociationDialog from '@/components/entity/EntityAssociationDialog.vue'
// AssociationCoverageRail removed — not used in this SFC (kept for future Phase 27+ work)
import { useTaskActivityStore } from '@/stores/taskActivityStore'
import { useEntityLinkStudio } from '@/composables/useEntityLinkStudio'
import {
  invalidateCustomerAssociationsQueries,
  useCustomerAssociationsQuery,
} from '@/composables/queries/useCustomerAssociationsQuery'
import { Users, Link, AlertCircle, CheckCircle, History, RefreshCw } from 'lucide-vue-next'

type CustomerPageTask = CustomerTaskDetailDTO | TaskDetail | TaskSummaryDto

// --- State ---
const selectedTask = ref<any | null>(null)
const drawerOpen = ref(false)
const taskActivity = useTaskActivityStore()
let unsubscribeTaskActivity: (() => void) | null = null
const router = useRouter()

const activeTasks = computed<CustomerPageTask[]>(() => taskActivity.activeTasksFor('CUSTOMER'))

async function loadTaskState() {
  track('btn:RefreshCustomers', 'click')
  await taskActivity.refreshNow({
    includeRecent: true,
    includeActive: true,
    entityTypes: ['CUSTOMER'],
    recentLimit: 50,
  })
}

onMounted(() => {
  unsubscribeTaskActivity = taskActivity.subscribeActivity({
    includeRecent: true,
    includeActive: true,
    entityTypes: ['CUSTOMER'],
    recentLimit: 50,
  })
  // useQuery already fetches on setup; do not refetch here.
})

onUnmounted(() => {
  unsubscribeTaskActivity?.()
  unsubscribeTaskActivity = null
})

function openTaskDrawer(task: any) {
  selectedTask.value = task
  drawerOpen.value = true
}

function taskEntityId(task: CustomerPageTask): string {
  return 'entityId' in task && task.entityId ? task.entityId : ('externalId' in task && task.externalId ? task.externalId : 'N/A')
}

function taskAcceptedAt(task: CustomerPageTask): string {
  return 'acceptedAt' in task && task.acceptedAt ? task.acceptedAt : ('createdAt' in task ? task.createdAt || '' : '')
}

function taskErrorMessage(task: CustomerPageTask): string | null {
  if ('lastErrorMessage' in task && task.lastErrorMessage) return task.lastErrorMessage
  if ('errorMessage' in task && task.errorMessage) return task.errorMessage
  return null
}

// --- Association table state (page owns filters; Colada owns the read) ---
const associationPage = ref(1)
const associationPageSize = ref(Number(localStorage.getItem('customers.pageSize') || 50))
const associationStatus = ref<'all' | 'associated' | 'unassociated' | 'syncing'>('all')
const searchQuery = ref('')
const sharedFilter = ref<'all' | 'associated' | 'unassociated' | 'syncing'>('all')
const syncingAllFeedback = ref('')
const syncingAllLoading = ref(false)
const actionError = ref('')
const syncingRow = ref<string | null>(null)
const lastRefreshAt = ref<Date | null>(null)

const {
  rows: associationRows,
  total: associationTotal,
  isLoading: associationLoading,
  errorMessage: associationQueryError,
  refetch: refetchAssociations,
} = useCustomerAssociationsQuery({
  page: associationPage,
  pageSize: associationPageSize,
  status: associationStatus,
})

// Prefer action errors; fall back to Colada query errors for the banner.
const error = computed(() => {
  if (actionError.value) return actionError.value
  return associationQueryError.value ?? ''
})

// Stamp last successful payload so the refresh label stays meaningful.
watch(
  () => associationTotal.value,
  () => {
    if (!associationLoading.value && !associationQueryError.value) {
      lastRefreshAt.value = new Date()
    }
  },
)

const filteredLytningRows = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return associationRows.value
  return associationRows.value.filter((row) => {
    const haystack = [row.lytsoftCustomerId, row.qboCustomerId, row.customerName, row.email]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase())
      .join(' ')
    return haystack.includes(q)
  })
})
// --- Tab and dialog state ---
const activeTab = ref<'lytning' | 'qbo'>('lytning')
const dialogOpen = ref(false)
const dialogSource = ref<'lytning' | 'qbo'>('lytning')
const dialogLytningEntity = ref<CustomerAssociationRow | null>(null)
const dialogQboEntity = ref<QboCustomerSearchResult | null>(null)

// --- QBO link studio (Phase 24) ---
const studio = useEntityLinkStudio<QboCustomerSearchResult>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchPredicate: searchQboCustomersPaged as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkAction: (qboId, lytId) => postCustomerLink(qboId, lytId) as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reverseLookupAction: (qboId, lytId) => getCustomerLink(qboId, lytId) as any,
  entityLabel: 'customer',
})

// --- Dialog handlers (Phase 24) ---
async function handleAssociated() {
  dialogOpen.value = false
  // Successful link: invalidate every customer-associations cache entry so the active list refetches.
  await Promise.all([invalidateCustomerAssociationsQueries(), loadTaskState()])
}

const associatedCount = computed(() =>
  associationStatus.value === 'unassociated' || associationStatus.value === 'syncing'
    ? 0
    : associationRows.value.filter(r => r.qboCustomerId).length
)

const unassociatedCount = computed(() =>
  associationStatus.value === 'associated' || associationStatus.value === 'syncing'
    ? 0
    : associationRows.value.filter(r => !r.qboCustomerId).length
)

const syncingCount = computed(() =>
  associationRows.value.filter(r => r.runningTaskId).length
)

// --- Association table actions ---
async function loadAssociations() {
  syncingAllFeedback.value = ''
  actionError.value = ''
  await refetchAssociations()
}

async function syncSingleCustomer(customer: CustomerAssociationRow) {
  if (customer.runningTaskId) return
  syncingRow.value = customer.lytsoftCustomerId
  actionError.value = ''
  try {
    await postCustomerAsync({ scope: 'single-sync', entityId: customer.lytsoftCustomerId })
    // Backend registration is not instantaneous; keep the 2s delay, then
    // targeted invalidation instead of a manual reload.
    setTimeout(() => {
      void invalidateCustomerAssociationsQueries()
    }, 2000)
  } catch (e) {
    actionError.value = (e as Error)?.message || 'Sync failed'
  } finally {
    syncingRow.value = null
  }
}

async function syncAllUnassociated() {
  syncingAllFeedback.value = ''
  syncingAllLoading.value = true
  actionError.value = ''
  try {
    const res = await postCustomersUnassociated()
    if (res.taskId) {
      syncingAllFeedback.value = `Bulk sync started (${res.totalUnassociated} customers in batches, task ${res.taskId})`
      // Backend registration is not instantaneous; same delay as single-sync.
      setTimeout(() => {
        void invalidateCustomerAssociationsQueries()
      }, 2000)
    } else {
      syncingAllFeedback.value = 'No unassociated customers to sync'
    }
  } catch (e) {
    actionError.value = (e as Error)?.message || 'Bulk sync failed'
  } finally {
    syncingAllLoading.value = false
  }
}

function setAssociationStatus(status: 'all' | 'associated' | 'unassociated' | 'syncing') {
  associationStatus.value = status
  associationPage.value = 1
  // Reactive key change drives Colada; clear local feedback without forced refetch.
  syncingAllFeedback.value = ''
  actionError.value = ''
}

function mapUiFilterToStudio(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  if (filter === 'associated') return 'linked' as const
  if (filter === 'unassociated') return 'unlinked' as const
  return filter
}

function onSharedFilterClick(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  // Cancel any pending debounced search so the filter click
  // is the single source of truth for issuing a new search
  // (setAssociationFilter triggers searchQbo internally).
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  track('btn:CustomerFilter', 'click')
  sharedFilter.value = filter
  if (activeTab.value === 'lytning') {
    setAssociationStatus(filter)
  } else {
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(filter))
  }
}

// Replay the shared filter when the active tab changes so filter state persists.
watch(activeTab, (tab) => {
  if (tab === 'lytning') {
    setAssociationStatus(sharedFilter.value)
  } else {
    // Sync studio.query BEFORE setAssociationFilter so the
    // composable's internal searchQbo picks up the typed query.
    // Cancel any pending debounced search to avoid duplicate requests.
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
      searchDebounceTimer = null
    }
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(sharedFilter.value))
  }
})

// Single searchQuery drives both tabs: Lytning filters in-memory; QBO debounces to studio.
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
watch(searchQuery, (q) => {
  if (activeTab.value === 'qbo') {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      void studio.searchQbo(q, 1)
    }, 300)
  }
})
function onPageSizeChange(size: number) {
  associationPageSize.value = size
  localStorage.setItem('customers.pageSize', String(size))
  associationPage.value = 1
  // Reactive key change drives Colada; clear local feedback without forced refetch.
  syncingAllFeedback.value = ''
  actionError.value = ''
}


function openLinkDialog(source: 'lytning' | 'qbo', entity?: CustomerAssociationRow | QboCustomerSearchResult) {
  dialogSource.value = source
  dialogLytningEntity.value = source === 'lytning' ? (entity as CustomerAssociationRow | null) : null
  dialogQboEntity.value = source === 'qbo' ? (entity as QboCustomerSearchResult | null) : null
  dialogOpen.value = true
}


function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}
</script>

<template>
  <div class="app-page app-page--narrow">
    <section class="app-page-header">
      <div class="app-page-heading">
        <div class="app-page-eyebrow">
          <Users class="h-4 w-4" />
          <span>Customer ledger</span>
        </div>
        <h1 class="app-page-title">Customer sync coverage</h1>
        <p class="app-page-subtitle">Sync customer records to QuickBooks and track async task progress.</p>

        <div class="page-badge-strip">
          <Button variant="ghost" size="sm" @click="() => { track('btn:ViewCustomerSyncHistory', 'click'); router.push('/app/tasks?entityType=CUSTOMER') }">
            <History class="h-3.5 w-3.5" />
            View sync history
          </Button>
        </div>
      </div>
      <div class="app-page-actions">
        <Button variant="secondary" @click="() => { track('btn:RefreshCustomers', 'click'); loadTaskState() }">
          <RefreshCw class="h-4 w-4" />
          Refresh
        </Button>
        <Button variant="outline" @click="() => { track('btn:ManageLinksCustomers', 'click'); activeTab = 'lytning' }">
          <Link class="h-4 w-4" />
          Manage Links
        </Button>
      </div>
    </section>
    <!-- Active tasks section -->
    <TaskDetailDrawer
      :task="selectedTask"
      :open="drawerOpen"
      @action-complete="loadTaskState"
      @close="drawerOpen = false"
    />
    <!-- Entity Association Dialog -->
    <EntityAssociationDialog
      :open="dialogOpen"
      entityType="customer"
      :source="dialogSource"
      :lytningEntity="dialogLytningEntity ?? undefined"
      :qboEntity="dialogQboEntity ?? undefined"
      @update:open="dialogOpen = $event"
      @associated="handleAssociated"
    />
    <!-- Association section -->
    <section class="assoc-section">
      <!-- Shared search + filter row (above both tabs) -->
      <div class="entity-search">
        <SearchInput
          v-model="searchQuery"
          label="Search customers"
          placeholder="Search by id, name, email"
          :loading="activeTab === 'qbo' && studio.loading.value"
        />
      </div>
      <div class="status-tabs" role="tablist" aria-label="Filter by association status">
        <button
          v-for="tab in ['all', 'associated', 'unassociated', 'syncing'] as const"
          :key="tab"
          type="button"
          role="tab"
          class="status-tab"
          :class="{
            'status-tab--active': sharedFilter === tab,
            'status-tab--disabled': tab === 'syncing' && activeTab === 'qbo',
          }"
          :disabled="tab === 'syncing' && activeTab === 'qbo'"
          :title="tab === 'syncing' && activeTab === 'qbo' ? 'Syncing is a Lytning concept — not available on the QBO tab' : undefined"
          :aria-selected="sharedFilter === tab"
          @click="onSharedFilterClick(tab)"
        >
          {{ tab.charAt(0).toUpperCase() + tab.slice(1) }}
        </button>
      </div>

      <!-- Main tabs -->
      <TabNav
        v-model="activeTab"
        :tabs="[
          { id: 'lytning', label: 'Lytning Customers', badge: unassociatedCount || undefined },
          { id: 'qbo', label: 'QBO Customers' }
        ]"
      />
      <div v-if="activeTab === 'lytning'">
        <!-- Loading state -->
        <div v-if="associationLoading" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading associations...</span>
        </div>

        <!-- Empty state -->
        <div v-else-if="filteredLytningRows.length === 0" class="tasks-empty">
          <div class="tasks-empty-symbol">
            <Users class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">{{ searchQuery ? 'No matches' : 'No customers found' }}</div>
          <div class="tasks-empty-copy">
            {{ searchQuery ? `No customers match "${searchQuery}". Try a different search term.` : 'Try a different filter or sync unlinked customers first.' }}
          </div>
        </div>

        <!-- Association table -->
        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>LytSoft ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>QBO ID</th>
                <th>Synced At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in filteredLytningRows" :key="customer.lytsoftCustomerId">
                <td class="td-mono">{{ customer.lytsoftCustomerId }}</td>
                <td>{{ customer.customerName }}</td>
                <td>{{ customer.email ?? '—' }}</td>
                <td>{{ customer.phone ?? '—' }}</td>
                <td>
                  <Badge v-if="customer.qboCustomerId" variant="success">
                    <CheckCircle class="h-3 w-3" />
                    Associated
                  </Badge>
                  <Badge v-else variant="warning">
                    <AlertCircle class="h-3 w-3" />
                    Unassociated
                  </Badge>
                </td>
                <td class="td-mono">{{ customer.qboCustomerId ?? '—' }}</td>
                <td>{{ formatDate(customer.syncedAt) }}</td>
                <td>
                  <div class="action-cell">
                    <div v-if="customer.runningTaskId" class="sync-indicator">
                      <RefreshCw class="h-4 w-4 spin" />
                      Syncing #{{ customer.runningTaskId }}
                    </div>
                    <div v-else class="action-buttons">
                      <Button
                        variant="outline"
                        size="sm"
                        @click="() => { track('btn:SyncCustomer', 'click'); syncSingleCustomer(customer) }"
                        :loading="syncingRow === customer.lytsoftCustomerId"
                      >
                        <RefreshCw class="h-4 w-4" />
                        Sync
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        @click="() => { track('btn:LinkCustomerLytning', 'click'); openLinkDialog('lytning', customer) }"
                      >
                        <Link class="h-4 w-4" />
                        Link
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <Pagination
          v-model:page="associationPage"
          v-model:pageSize="associationPageSize"
          :total="associationTotal"
          :loading="associationLoading"
          label="customers"
          :page-size-options="[50, 100, 200]"
          track-label="btn:CustomerPage"
          @update:pageSize="onPageSizeChange"
        />
      </div>

      <!-- QBO tab content -->
      <div v-if="activeTab === 'qbo'" class="qbo-tab">
        <Alert v-if="studio.error.value" variant="error" title="Search failed" class="page-alert">
          {{ studio.error.value }}
        </Alert>

        <div v-if="studio.results.value.length === 0 && !studio.loading.value && !studio.error.value" class="tasks-empty">
          <div class="tasks-empty-symbol">
            <RefreshCw class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">No QBO customers found</div>
          <div class="tasks-empty-copy">
            Try a different search term.
          </div>
        </div>

        <div v-else-if="studio.loading.value" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading QBO customers...</span>
        </div>

        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in studio.results.value" :key="customer.qboCustomerId">
                <td>{{ customer.name }}</td>
                <td>{{ customer.email ?? '—' }}</td>
                <td>{{ customer.phone ?? '—' }}</td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    @click="() => { track('btn:LinkCustomerQbo', 'click'); openLinkDialog('qbo', customer) }"
                  >
                    <Link class="h-4 w-4" />
                    Link
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>

          <Pagination
            :page="studio.page.value"
            :pageSize="studio.size.value"
            :total="studio.totalCount.value"
            :loading="studio.loading.value"
            label="QBO customers"
            :page-size-options="[20, 50]"
            track-label="btn:QboCustomerPage"
            @update:pageSize="studio.setSize"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>

.page-badge-strip {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

/* Action cell */
.action-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* QBO tab */
.qbo-tab {
  margin-top: 1rem;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 2rem;
  color: var(--color-ink-soft);
}

.status-tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.status-tab {
  padding: 0.375rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-ink-soft);
  cursor: pointer;
  transition: all 0.15s;
}

.status-tab:hover {
  background: var(--color-surface-muted);
}

.status-tab--active {
  background: var(--color-ink);
  color: var(--color-surface);
  border-color: var(--color-border-strong);
}

.status-tab--disabled,
.status-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-tab--disabled:hover,
.status-tab:disabled:hover {
  background: var(--color-surface);
}



.assoc-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 2rem;
  color: var(--color-ink-soft);
}

.assoc-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-md);
}

.assoc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.assoc-table th {
  text-align: left;
  padding: 0.625rem 0.75rem;
  background: var(--color-surface-muted);
  border-bottom: 1px solid var(--color-border-soft);
  font-weight: 600;
  color: var(--color-ink);
  white-space: nowrap;
}

.assoc-table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid var(--color-border-soft);
  color: var(--color-ink);
}

.assoc-table tr:last-child td {
  border-bottom: none;
}

.td-mono {
  font-family: monospace;
  font-size: 0.7rem;
}

.sync-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-ink-soft);
  font-size: 0.75rem;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}

</style>
